import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { employeeService } from '../employeeService';

export function useEmployees(
  page = 1,
  limit = 20,
  filters?: { search?: string; departmentId?: string; designationId?: string; specializationId?: string; status?: string; payrollEnabled?: string; websiteRole?: string }
) {
  return useQuery({
    queryKey: ['employees', page, limit, filters],
    queryFn: () => employeeService.getEmployees(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useEmployee(id: string) {
  return useQuery({
    queryKey: ['employees', id],
    queryFn: () => employeeService.getEmployee(id),
    enabled: !!id,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: employeeService.createEmployee,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => employeeService.updateEmployee(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['employees', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      // Staff edits sync name/roles onto the linked user account.
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => employeeService.deleteEmployee(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/** Enable or disable payroll for many employees in one request. */
export function useBulkUpdateEmployeePayroll() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, payrollEnabled }: { ids: string[]; payrollEnabled: boolean }) =>
      employeeService.setBulkPayrollEnabled(ids, payrollEnabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

/** Bulk-delete employees as a single silent mutation (loops the single-delete endpoint). */
export function useBulkDeleteEmployees() {
  const queryClient = useQueryClient();
  return useMutation({
    meta: { hideToast: true },
    mutationFn: async (ids: string[]) => {
      for (const id of ids) {
        await employeeService.deleteEmployee(id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useTrashedEmployees(
  page = 1,
  limit = 20,
  filters?: { search?: string; departmentId?: string; designationId?: string; specializationId?: string; status?: string }
) {
  return useQuery({
    queryKey: ['employees', 'trash', page, limit, filters],
    queryFn: () => employeeService.getTrashedEmployees(page, limit, filters),
    placeholderData: (previous) => previous,
    staleTime: 30000,
  });
}

export function useRestoreEmployees() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => employeeService.restoreEmployees(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function usePermanentlyDeleteEmployees() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => employeeService.permanentlyDeleteEmployees(ids),
    meta: { hideToast: true },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}
